import { FunctionComponent, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { authSelector } from "@/pages/Public/redux/auth.slice";
import { useAppSelector } from "@/redux/hooks";

import { LoadingOverlay } from "@/components";

import { useGetDashboardQuery } from "@/pages/Private/redux/dashboard/dashboard.api";
import { dashboardSelector, emptyDashboard } from "@/pages/Private/redux/dashboard/dashboard.slice";

import { useOnboardingItems } from "../../hooks/useOnboardingItems";
import {
	DASHBOARD_REFETCH_MIN_AGE_S,
	shouldRefetchOnVisibility,
} from "../../utils/refetchThrottle";
import { BriefingStrip } from "../../components/BriefingStrip";
import { ExtensionPromoBox } from "../../components/ExtensionPromoBox";
import { IssuesBox } from "../../components/IssuesBox";
import { SuggestionsBox } from "../../components/SuggestionsBox";
import { partitionDashboardIssues } from "../../utils/dashboardIssueGroups";
import { NewDraftsBox } from "../../components/NewDraftsBox";
import { OnboardingBox } from "../../components/OnboardingBox";
import { ReEngageBox } from "../../components/ReEngageBox";
import { RepliesBox } from "../../components/RepliesBox";
import { TourResumeBanner } from "../../components/TourResumeBanner";

const buildGreeting = (firstName: string | undefined) => {
	const hour = new Date().getHours();
	let prefix = "Good morning";

	if (hour >= 12 && hour < 18) {
		prefix = "Good afternoon";
	} else if (hour >= 18 || hour < 5) {
		prefix = "Good evening";
	}

	return firstName ? `${prefix}, ${firstName}` : prefix;
};

export const DashboardOverview: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`dashboard.${key}`);

	const { user } = useAppSelector(authSelector);
	const { data: stateData, hasLoaded } = useAppSelector(dashboardSelector);

	const { isLoading, isError, isFetching, fulfilledTimeStamp, refetch } = useGetDashboardQuery(
		undefined,
		{
			// Age threshold (not `true`): a mount with fresh cached data must not
			// re-fire the endpoint — mount + visibilitychange used to double-fire it.
			refetchOnMountOrArgChange: DASHBOARD_REFETCH_MIN_AGE_S,
		}
	);

	useEffect(() => {
		const onVisibilityChange = () => {
			if (
				document.visibilityState === "visible" &&
				shouldRefetchOnVisibility({ isFetching, fulfilledTimeStamp, now: Date.now() })
			) {
				refetch();
			}
		};

		document.addEventListener("visibilitychange", onVisibilityChange);

		return () => document.removeEventListener("visibilitychange", onVisibilityChange);
	}, [refetch, isFetching, fulfilledTimeStamp]);

	const greeting = useMemo(() => buildGreeting(user?.firstName), [user?.firstName]);

	const data = stateData ?? emptyDashboard;
	const onboardingItems = useOnboardingItems(data.firstSteps);

	if (isLoading && !hasLoaded) {
		return (
			<div className="bg-brand-white min-h-full -mx-4 -mt-3 -mb-4 px-4 pt-3 pb-4">
				<LoadingOverlay />
			</div>
		);
	}

	// Suggestions are advisory, not faults — they render in their own box and are deliberately
	// left out of the "issues found" count in the briefing strip (PREC-3594).
	const { issues, suggestions } = partitionDashboardIssues(data.issues);
	const onboardingDone = onboardingItems.filter((item) => item.done).length;
	const onboardingTotal = onboardingItems.length;
	const repliesTotal = data.myReplies.total + data.companyReplies.total;

	return (
		<div className="bg-brand-white min-h-full -mx-4 px-4 pt-3">
			<div className="max-w-[1240px] mx-auto py-4 lg:px-2 lg:py-6">
				<div className="mb-6">
					<h1 className="text-2xl font-semibold text-text">{greeting}</h1>
					<p className="text-sm text-text-meta">{ts("subline")}</p>
					{isError && <p className="text-xs text-status-error mt-2">{ts("loadError")}</p>}
				</div>

				<BriefingStrip
					issuesCount={issues.length}
					newDraftsTotal={data.newDrafts.total}
					onboardingDone={onboardingDone}
					onboardingTotal={onboardingTotal}
					reEngageTotal={data.reEngage?.total ?? 0}
					repliesTotal={repliesTotal}
				/>

				<div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
					<div className="flex flex-col gap-5 min-w-0">
						<RepliesBox company={data.companyReplies} mine={data.myReplies} />
						<NewDraftsBox
							lists={data.listsWithNewProspects}
							newDraftsTotal={data.newDrafts.total}
						/>
						<ReEngageBox section={data.reEngage ?? { items: [], total: 0 }} />
					</div>

					<div className="flex flex-col gap-5 min-w-0">
						<TourResumeBanner />
						<OnboardingBox items={onboardingItems} />
						<IssuesBox issues={issues} />
						<SuggestionsBox suggestions={suggestions} />
						<ExtensionPromoBox installed={Boolean(data.firstSteps.hasInstalledBrowserExtension)} />
					</div>
				</div>
			</div>
		</div>
	);
};
